import React, { useContext, useMemo, useRef } from 'react'

import Skeleton from '@mui/material/Skeleton'
import { Box, Icon } from '@vividseats/vivid-ui-kit'
import { useIntersectionObserver } from 'usehooks-ts'

import filterContext from '@/components/pages/production/components/context'
import StyledChip from '@/components/shared/styled-chip/component'
import ListingsContext from '@/context/listings/context'
import { Group } from '@/context/listings/types'
import colors from '@/design-system/themes/colors.json'
import { useDesktopMediaQuery } from '@/utils'
import { convertMapColor } from '@/utils/map-colors'

import styles from './styles.module.scss'

const ROW_BUTTON_SCROLL_DISTANCE = 0.5

const sortGroupsByQuantity = (groupA: Group, groupB: Group) => {
    const ticketsA = Number(groupA.q)
    const ticketsB = Number(groupB.q)

    return ticketsB - ticketsA
}

type GroupFilterChipRowProps = { showDivider?: boolean }

const GroupFilterChipRow: React.FC<GroupFilterChipRowProps> = ({ showDivider }) => {
    const isDesktop = useDesktopMediaQuery()

    const {
        state: { data: listingsData },
    } = useContext(ListingsContext)

    const {
        dispatch: { onShowSimilarSectionsAction },
        state: { selectedGroups },
    } = useContext(filterContext)

    // Sometimes there are groups with no name or listings that we have to filter out
    // Also filter out groups with no inventory, as it would just show empty groups
    const groups = useMemo(() => {
        return (listingsData?.groups ?? [])
            .filter(({ n, q }) => n.trim() !== '' && Number(q) > 0)
            .sort(sortGroupsByQuantity)
    }, [listingsData?.groups])

    // for context, there's usually only one item in the global array
    const listingsCount = Number(listingsData?.global[0]?.listingCount ?? 0)

    // using the loading property on the listings context doesn't work
    const shouldShowSkeletonLoader = !listingsData

    const hideChips = !shouldShowSkeletonLoader && (groups.length === 0 || listingsCount < 10)

    const chipRowRef = useRef<HTMLDivElement | null>(null)
    const firstChipRef = useRef<HTMLDivElement | null>(null)
    const lastChipRef = useRef<HTMLDivElement | null>(null)

    const firstChipIntersectionObserver = useIntersectionObserver(firstChipRef, {
        threshold: 1,
        root: chipRowRef.current,
    })
    const lastChipIntersectionObserver = useIntersectionObserver(lastChipRef, {
        threshold: 1,
        root: chipRowRef.current,
    })

    const firstChipIsVisible = firstChipIntersectionObserver?.isIntersecting
    const lastChipIsVisible = lastChipIntersectionObserver?.isIntersecting

    const handleChipClick = (groupId: string, selected: boolean) => {
        onShowSimilarSectionsAction({ groupId, selected })
    }

    const onScrollChipsLeft = () => {
        if (!chipRowRef.current) {
            return
        }

        const scrollDistance =
            chipRowRef.current.scrollLeft - chipRowRef.current.clientWidth * ROW_BUTTON_SCROLL_DISTANCE

        chipRowRef.current.scroll({ left: scrollDistance, behavior: 'smooth' })
    }

    const onScrollChipsRight = () => {
        if (!chipRowRef.current) {
            return
        }

        const scrollDistance =
            chipRowRef.current.scrollLeft + chipRowRef.current.clientWidth * ROW_BUTTON_SCROLL_DISTANCE

        chipRowRef.current.scroll({ left: scrollDistance, behavior: 'smooth' })
    }

    if (shouldShowSkeletonLoader) {
        // This is design tech debt, update the loader once we have them in design system
        const ChipSkeleton = () => (
            <Skeleton
                height="2rem" // 32px
                width="8.5rem" // 136px
                animation="wave"
                variant="rounded"
                sx={{
                    '&.MuiSkeleton-rounded': {
                        borderRadius: '16px',
                        // when we get rid of ui-kit, this color will need to change
                        // for now match with UI-kit skeleton colors
                        backgroundColor: colors.gray[200],
                    },
                }}
            />
        )

        return (
            <>
                {showDivider && <div className={styles.divider} />}
                <div className={styles.skeletonContainer} data-testid="group-chip-skeleton-loader">
                    {[...Array(3)].map((_, index) => (
                        <ChipSkeleton key={index} />
                    ))}
                </div>
            </>
        )
    }

    if (hideChips) {
        return null
    }

    return (
        <>
            {showDivider && <div className={styles.divider} />}
            <div className={styles.container} data-testid="group-select-chips">
                {/*
                 * this complicated setup with intersection observers exists because a desktop user without
                 * a trackpad, touchscreen, fancy mouse, ect, cannot scroll the list. These buttons allow
                 * them to scroll.
                 */}
                {isDesktop && !firstChipIsVisible && (
                    <div className={styles.leftArrowContainer}>
                        <button
                            className={styles.leftArrow}
                            onClick={onScrollChipsLeft}
                            data-testid="scroll-chips-left"
                        >
                            <Icon type="back" size="sm" />
                        </button>
                    </div>
                )}
                {isDesktop && !lastChipIsVisible && (
                    <div className={styles.rightArrowContainer}>
                        <div className={styles.fader}></div>
                        <button
                            className={styles.rightArrow}
                            onClick={onScrollChipsRight}
                            data-testid="scroll-chips-right"
                        >
                            <Icon type="back" size="sm" />
                        </button>
                    </div>
                )}
                <div className={styles.rowContainer} ref={chipRowRef}>
                    <Box className={styles.chipRow} data-testid="chip-row">
                        {groups?.map(({ i: id, n: name, c: rawColor }, index) => {
                            const chipIsSelected = selectedGroups.includes(id)

                            const color = convertMapColor(rawColor)

                            const groupColorLabel = (
                                <Box className={styles.chipContents}>
                                    <div className={styles.mapColor} style={{ backgroundColor: color }}></div>
                                    {name}
                                </Box>
                            )

                            const refProp =
                                index === 0 ? firstChipRef : index === groups.length - 1 ? lastChipRef : undefined

                            return (
                                <div key={id} ref={refProp} data-testid={`group-chip-wrapper-${id}`}>
                                    <StyledChip
                                        label={groupColorLabel}
                                        variant={chipIsSelected ? 'filled' : 'outlined'}
                                        onClick={() => handleChipClick(id, !chipIsSelected)}
                                        data-testid={`group-chip-${id}`}
                                    />
                                </div>
                            )
                        })}
                    </Box>
                </div>
            </div>
        </>
    )
}

export default GroupFilterChipRow
